import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import { loadProfileStatus, clearProfileStatus } from '../services/profileStatus';
import { resizeImage, formatPhone, profileFromRecord, mergeProfileForSave, displayNameFrom } from '../services/profileData';
import PhoneInput from './PhoneInput';

// "Start with the basics" checklist with the missing required items filled in right here.
// Saves exactly like My Profile: users/{uid}/profile (profileToSave) + displayName; insurance is optional and stays on the Profile page.
// Each item disappears once it is saved. Phone-friendly: everything stacks, nothing wider than the screen.
const box = { background: '#ffffff', border: '1px solid #d8c9a8', borderLeft: '4px solid #D4AF37', borderRadius: 10, padding: 16, marginBottom: 16, boxSizing: 'border-box', maxWidth: '100%', overflowWrap: 'anywhere' };
const h2 = { fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: '0 0 8px' };
const muted = { color: '#7f7f7f', fontSize: 14, margin: '0 0 12px' };
const label = { display: 'block', fontSize: 13, color: '#1a1a1a', fontWeight: 600, marginBottom: 4 };
const input = { width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d8c9a8', borderRadius: 6, fontSize: 16, marginBottom: 12 };
const btn = { backgroundColor: '#1a1a1a', color: '#D4AF37', border: 'none', padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 15 };
const ghost = { ...btn, background: '#ffffff', color: '#5B6270', border: '1px solid #D5D9E0' };
const thumb = { width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #D4AF37', display: 'block', marginBottom: 6 };
const done = { color: '#2e7d32', fontSize: 14, margin: '0 0 8px' };

const FIELDS = { 'First name': 'firstName', 'Last name': 'lastName', Title: 'title', 'Phone number': 'phone', Email: 'contactEmail' };
const PHOTOS = { 'Headshot photo': 'profilePhotoUrl', 'Celebration photo': 'celebrationPhotoUrl' };

export default function ProfileChecklist({ uid, onboarding = false, showGoToProfile = false, onSaved, onComplete }) {
  const navigate = useNavigate();
  const [missing, setMissing] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const refresh = async () => {
    clearProfileStatus(uid);
    const status = await loadProfileStatus(uid);
    setMissing(status.missing);
    if (!status.missing.length && onComplete) onComplete();
    return status.missing;
  };
  useEffect(() => { if (uid) refresh().catch(() => setMissing([])); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const SAVED = "Basics saved. Your profile isn't complete yet. Go to Profile in the left menu to fill in the rest (insurance providers, review links, and more).";
  if (!missing) return null;
  if (!missing.length) {
    if (!msg) return null;
    return (
      <div style={box}>
        <p style={{ ...done, margin: 0 }}>✓ {SAVED}</p>
        {showGoToProfile && (<div style={{ marginTop: 12 }}><button type="button" style={ghost} onClick={() => navigate('/profile', { state: { onboarding } })}>Go to Profile</button></div>)}
      </div>
    );
  }

  const val = (k) => edits[k] ?? '';
  const change = (e) => { const { name, value } = e.target; setEdits((x) => ({ ...x, [name]: name === 'phone' ? formatPhone(value) : value })); };
  const upload = async (e, field) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try { setError(null); const dataUrl = await resizeImage(file); setEdits((x) => ({ ...x, [field]: dataUrl })); }
    catch (err) { setError('That photo could not be loaded. Try a different image.'); }
  };
  const fieldItems = missing.filter((m) => FIELDS[m] || PHOTOS[m]);
  const hasEdits = Object.values(edits).some((v) => String(v || '').trim());

  const save = async () => {
    setSaving(true); setError(null); setMsg(null);
    try {
      // Same as My Profile's save: start from the saved profile, apply the new values, write it the same way.
      const snap = await get(ref(database, `users/${uid}`));
      const form = { ...profileFromRecord(snap.exists() ? snap.val() : {}) };
      Object.entries(edits).forEach(([k, v]) => { if (String(v || '').trim()) form[k] = v; });
      await set(ref(database, `users/${uid}/profile`), mergeProfileForSave((snap.exists() && snap.val().profile) || {}, form));
      if (displayNameFrom(form)) await update(ref(database, `users/${uid}`), { displayName: displayNameFrom(form) });
      setEdits({});
      if (onSaved) onSaved(form);
      const left = await refresh();
      setMsg(left.length ? 'Saved. A few basics are still left.' : SAVED);
    } catch (err) {
      console.error('Could not save profile items', err);
      setError('Could not save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div style={box}>
      <h2 style={h2}>Start with the basics</h2>
      <p style={muted}>Just a few basics to get you started. You'll complete your full profile later. Fill these in right here, then tap Save:</p>
      {fieldItems.map((item) => {
        const k = FIELDS[item] || PHOTOS[item];
        if (PHOTOS[item]) {
          return (
            <div key={item} style={{ marginBottom: 12 }}>
              <label style={label}>{item}</label>
              {edits[k] && <img src={edits[k]} alt={item} style={thumb} />}
              <input type="file" accept="image/*" onChange={(e) => upload(e, k)} style={{ maxWidth: '100%', fontSize: 14 }} />
            </div>
          );
        }
        return (
          <div key={item}>
            <label style={label} htmlFor={`pc-${k}`}>{item}</label>
            {k === 'phone'
              ? <PhoneInput id={`pc-${k}`} name={k} style={input} value={val(k)} onChange={change} placeholder="(239) 555-1234" />
              : <input id={`pc-${k}`} name={k} type={k === 'contactEmail' ? 'email' : 'text'} style={input} value={val(k)} onChange={change} placeholder={k === 'title' ? 'e.g. REALTOR®' : ''} />}
          </div>
        );
      })}
      {fieldItems.length > 0 && (
        <button type="button" style={{ ...btn, opacity: hasEdits ? 1 : 0.6 }} onClick={save} disabled={saving || !hasEdits}>{saving ? 'Saving...' : 'Save'}</button>
      )}
      {msg && <p style={{ ...done, marginTop: 8 }}>✓ {msg}</p>}
      {error && <p style={{ color: '#b00020', fontSize: 14, marginTop: 8 }}>{error}</p>}
      {showGoToProfile && (
        <div style={{ marginTop: 12 }}>
          <button type="button" style={ghost} onClick={() => navigate('/profile', { state: { onboarding } })}>Go to Profile</button>
        </div>
      )}
    </div>
  );
}
